import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { SnackbarApi, SnackbarOptions } from './props.js'
import { CloseIcon } from './icons.js'

interface SnackbarMessage {
  id: string
  message: string
  tone: 'neutral' | 'error' | 'success'
  duration: number
  action?: { label: string; onClick: () => void }
}

const SnackbarContext = createContext<SnackbarApi | null>(null)

export function useSnackbar(): SnackbarApi {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}

interface SnackbarProviderProps {
  children: ReactNode
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [messages, setMessages] = useState<SnackbarMessage[]>([])

  const show = useCallback(
    (message: string, options?: SnackbarOptions) => {
      const id = Math.random().toString(36).substr(2, 9)
      const tone = options?.tone ?? 'neutral'
      const duration = options?.duration ?? 4000
      const action = options?.action

      const newMessage: SnackbarMessage = {
        id,
        message,
        tone,
        duration,
        action,
      }

      setMessages((prev) => [...prev, newMessage])

      if (duration > 0) {
        setTimeout(() => {
          setMessages((prev) => prev.filter((m) => m.id !== id))
        }, duration)
      }
    },
    []
  )

  const error = useCallback(
    (message: string, options?: Omit<SnackbarOptions, 'tone'>) => {
      show(message, { ...options, tone: 'error' })
    },
    [show]
  )

  const success = useCallback(
    (message: string, options?: Omit<SnackbarOptions, 'tone'>) => {
      show(message, { ...options, tone: 'success' })
    },
    [show]
  )

  const api: SnackbarApi = { show, error, success }

  const removeMessage = (id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id))
  }

  return (
    <SnackbarContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] start-6 end-6 md:start-auto md:w-80 z-40 space-y-3 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          {messages.map((msg) => (
            <SnackbarItem
              key={msg.id}
              message={msg}
              onClose={() => removeMessage(msg.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </SnackbarContext.Provider>
  )
}

interface SnackbarItemProps {
  message: SnackbarMessage
  onClose: () => void
}

function SnackbarItem({ message, onClose }: SnackbarItemProps) {
  const dotColorClasses: Record<string, string> = {
    neutral: 'bg-on-surface-variant',
    error: 'bg-error',
    success: 'bg-success',
  }

  return (
    <div
      className="bg-surface-highest border border-outline-variant rounded-lg shadow-elev-3 px-4 py-3 text-body-md text-on-surface animate-slide-up pointer-events-auto flex items-center justify-between gap-4"
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Leading tone indicator dot */}
        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColorClasses[message.tone]}`} />
        <span>{message.message}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {message.action && (
          <button
            onClick={() => {
              message.action?.onClick()
              onClose()
            }}
            className="text-label-lg text-on-surface-variant hover:text-on-surface transition-colors interactive"
          >
            {message.action.label}
          </button>
        )}
        <button
          onClick={onClose}
          aria-label="Close snackbar"
          className="text-on-surface-variant hover:text-on-surface transition-colors interactive"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  )
}
